import { useState } from "react";
import { motion } from "framer-motion";
import { useApp } from "../lib/store";
import { Button } from "../components/ui/button";
import { MessageCircleQuestion, ChevronDown, Compass } from "lucide-react";

const FAQ_KEYS = Array.from({ length: 10 }, (_, i) => i + 1);

function FaqItem({ n, idx }) {
  const { t } = useApp();
  const [open, setOpen] = useState(idx === 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: idx * 0.03 }}
      className="bg-white border border-gray-200 rounded-xl overflow-hidden"
      data-testid={`faq-item-${n}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${n}`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-amber-50/40 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-sm" style={{ fontFamily: "Work Sans" }}>{t(`faq_q${n}`)}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed" data-testid={`faq-answer-${n}`}>
          {t(`faq_a${n}`)}
        </div>
      )}
    </motion.div>
  );
}

export default function Faq() {
  const { t } = useApp();
  return (
    <div className="space-y-6 max-w-3xl" data-testid="faq-page">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1B2A4A] via-[#22345c] to-[#2f4a7a] text-white p-8 grain">
        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#F5A623]/25 blur-3xl animate-float-slow" />
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative">
          <div className="flex items-center gap-2 text-[#F5A623] text-sm font-semibold tracking-wide mb-2">
            <MessageCircleQuestion className="h-4 w-4" /> {t("faq")}
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }}>{t("faq_title")}</h1>
          <p className="text-white/65 mt-2 max-w-2xl text-sm">{t("faq_subtitle")}</p>
        </motion.div>
      </div>

      <div className="space-y-3">
        {FAQ_KEYS.map((n, idx) => <FaqItem key={n} n={n} idx={idx} />)}
      </div>

      <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-5">
        <p className="text-sm text-gray-500">{t("faq_intro")}</p>
        <Button
          data-testid="faq-start-tour-btn"
          onClick={() => window.dispatchEvent(new Event("lf-start-tour"))}
          className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full px-5 shrink-0">
          <Compass className="h-4 w-4 mr-2" /> {t("restart_tour")}
        </Button>
      </div>
    </div>
  );
}
